<template>
  <div class="pdf-container">
  </div>
</template>

<script>
import * as echarts from "echarts";

import jsPDF from "jspdf";
import "jspdf-autotable";

export default {
  name: "PDFGenerator",
  props: {
    patient: Object,
    facescan_id: String,
    landmark_id: Number,
    scanDate: String,
    imagingModality: String,
    snapshots: Array,
    pflAnalysisResult: Object,
    malePFLChartOptions: Object,
    femalePFLChartOptions: Object,
    heatmapSnapshot: String,
    mode1PlotImage: String,
    firstPrincipalComponent: String,
    mode1Percentile: Number,
    classificationSnapshot: String,
  },
  data() {
    return {
      age: null,
    };
  },
  methods: {
    async calculateAndSetAge() {
      const fasdBackendURL =
        localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
      const token = localStorage.getItem("userJWtoken");

      const response = await fetch(
        `${fasdBackendURL}/api/facescans/patientAge/${this.facescan_id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const age = await response.json();
      this.age = parseFloat(age).toFixed(1); 
    },
    async generatePFLChartBase64(options) {
      const chartContainer = document.createElement("div");
      chartContainer.style.width = "800px";
      chartContainer.style.height = "600px";
      chartContainer.style.position = "absolute";
      chartContainer.style.top = "-10000px"; // Offscreen
      document.body.appendChild(chartContainer);

      const chartInstance = echarts.init(chartContainer);
      chartInstance.setOption({
        ...options,
        backgroundColor: "#FFFFFF", 
      });
      await new Promise((resolve) => setTimeout(resolve, 1000)); 
      const base64Image = chartInstance.getDataURL({
        type: "jpeg",
        pixelRatio: 2,
        backgroundColor: "#FFFFFF", 
      });

      chartInstance.dispose();
      document.body.removeChild(chartContainer);

      return base64Image;
    },


  async generatePDF() {
      const doc = new jsPDF();

       ////////////////////////////////////////////////Header and Footer Images//////////////////////////////////////////////////
      const addHeaderAndFooter = (pageNumber) => {
        // Header Images
        const headerImage = require("../../../assets/logo6.png");
        doc.addImage(headerImage, "JPEG", 4, 5, 15, 14);

        // Footer Images
        const footerImages = [
          { src: require("../../../assets/Picture1.jpg"), x: 3, y: 280, width: 45, height: 14 },
          { src: require("../../../assets/Picture2.png"), x: 49, y: 280, width: 37, height: 14 },
          { src: require("../../../assets/Picture3.png"), x: 184, y: 274, width: 23, height: 21 },
        ];

        footerImages.forEach((image) => {
          doc.addImage(image.src, "JPEG", image.x, image.y, image.width, image.height);
        });

        // Footer Page Number
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.setFontSize(10);
        doc.setTextColor(119, 119, 119);
        doc.text(`Page ${pageNumber}`, pageWidth / 2, pageHeight - 10, { align: "center" });
      };

      ///////////////////////////////////////////////////////First Page/////////////////////////////////////////////////////////////
      addHeaderAndFooter(1);
      doc.setFontSize(18); 
      doc.setTextColor(39, 48, 95);
      doc.text("FaceScreen Report", 105, 28, { align: "center" }); 

      // current date and time
      const currentDate = new Date();
      const formattedDate = `${currentDate.toLocaleDateString()}, ${currentDate.toLocaleTimeString()}`;
      doc.setFontSize(10);
      doc.setTextColor(119, 119, 119);
      doc.text(`Generated on: ${formattedDate}`, 105, 34, { align: "center" });


      //////////////////////////////////////////////////Subject Details Section//////////////////////////////////////////////////////
      doc.setFontSize(14);
      doc.setTextColor(39, 48, 95);
      doc.text("Subject Details", 10, 55, { align: "left" }); 
      // Line
      const pageWidth = doc.internal.pageSize.getWidth();
      doc.setDrawColor(39, 48, 95); 
      doc.line(10, 58, pageWidth - 10, 58); 
      // Subject details Table
      const tableData = [
        ["Subject ID", this.patient.patient_identifier],
        ["Name", `${this.patient.name || "N/A"} ${this.patient.surname}`],
        ["Sex", this.patient.sex],
        ["Ethnicity", this.patient.ethnicity],
        ["Date of Birth", this.patient.dob],
        ["Age", `${this.age} years old`],
      ];

      doc.autoTable({
        head: [["Field", "Value"]],
        body: tableData,
        startY: 63, 
        styles: {
          fontSize: 10, 
        },
        headStyles: {
          fillColor: [0, 78, 173], 
          textColor: [255, 255, 255], 
        },
      });

      //////////////////////////////////////////////////FaceScan Section//////////////////////////////////////////////////////
      doc.setFontSize(14);
      doc.setTextColor(39, 48, 95);
      doc.text("Face Scan 3D Snapshots", 10, 130, { align: "left" }); 

      // Line
      doc.setDrawColor(39, 48, 95); 
      doc.line(10, 133, pageWidth - 10, 133); 

      // FaceScan Table
      const FaceScanTable = [
              ["Scan Date:", this.scanDate],
              ["Imaging Modality:", this.imagingModality],
            ];
            doc.autoTable({
              body: FaceScanTable,
              startY: 138, 
              styles: {
                fontSize: 10, 
              },
            });

     // Snapshots
      let snapshotStartY = doc.previousAutoTable.finalY + 10; 
      const snapshotWidth = 60; 
      const snapshotHeight = 40; 
      const shorterWidth = 50; 
      const shorterHeight = 22; 
      const snapshotSpacing = 5; 

      // First row
      let x = 10; 
      let y = snapshotStartY; 

      this.snapshots.forEach((snapshot, index) => {
        if (index < 3) {
          // First row
          drawRoundedRectangle(doc, x, y, snapshotWidth, snapshotHeight, 5); 
          doc.addImage(snapshot, "JPEG", x, y, snapshotWidth, snapshotHeight);
          x += snapshotWidth + snapshotSpacing; 
        } else {
          // Second row
          if (index === 3) {
            const totalWidth = 2 * shorterWidth + snapshotSpacing;
            x = (pageWidth - totalWidth) / 2; 
            y += snapshotHeight + snapshotSpacing + 5;
          }
          drawRoundedRectangle(doc, x, y, shorterWidth, shorterHeight, 10); 
          doc.addImage(snapshot, "JPEG", x, y, shorterWidth, shorterHeight);
          x += shorterWidth + snapshotSpacing; 
        }
      });
      function drawRoundedRectangle(doc, x, y, width, height, radius) {
        doc.setDrawColor(39, 48, 95); 
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(x, y, width, height, radius, radius, 'FD'); 
      }

     ////////////////////////////////////////////////////Second Page/////////////////////////////////////////////////////////////////
      doc.addPage();
      addHeaderAndFooter(2);
      // PFL Section
      doc.setFontSize(14);
      doc.setTextColor(39, 48, 95);
      doc.text("PFL Analysis Results", 10, 35, { align: "left" }); 
      // Line
      doc.setDrawColor(39, 48, 95); 
      doc.line(10, 38, pageWidth - 10, 38); 
      // PFL Table
      const PFLTable = [
        ["Subject PFL", this.pflAnalysisResult.PFL.toFixed(2)],
        ["Z-score", this.pflAnalysisResult.PFLzScore.toFixed(2)],
        ["Percentile", this.pflAnalysisResult.PFLpercentile.toFixed(2)],
      ];

      doc.autoTable({
        head: [["Subject PFL Parameters", "Value"]],
        body: PFLTable,
        startY: 43, 
        styles: {
          fontSize: 10, 
        },
        headStyles: {
          fillColor: [0, 78, 173], 
          textColor: [255, 255, 255], 
        },
      });
      // PFL Chart 
      const chartOptions = this.patient.sex === "male" ? this.malePFLChartOptions : this.femalePFLChartOptions;
      const pflChartImage = await this.generatePFLChartBase64(chartOptions);
      const chartY = doc.previousAutoTable.finalY + 10;
      doc.addImage(pflChartImage, "JPEG", 10, chartY, 190, 140); 

      ////////////////////////////////////////////////////Third Page/////////////////////////////////////////////////////////////////
      doc.addPage();
      addHeaderAndFooter(3);

      // Heatmap Section
      doc.setFontSize(14);
      doc.setTextColor(39, 48, 95);
      doc.text("Heatmap Visualization", 10, 35, { align: "left" });

      // Line
      doc.setDrawColor(39, 48, 95);
      doc.line(10, 38, pageWidth - 10, 38);

      // Heatmap
      const heatmapY = 43; 
      if (this.heatmapSnapshot) {
          const heatmapWidth = 160; 
          const heatmapHeight = 135;
          doc.addImage(this.heatmapSnapshot, "JPEG", 20, heatmapY, heatmapWidth, heatmapHeight);
      } else {
          doc.setFontSize(10);
          doc.setTextColor(119, 119, 119);
          doc.text("No Heatmap Available", 10, heatmapY + 10);
      }  
      ////////////////////////////////////////////////////Fourth Page/////////////////////////////////////////////////////////////////
      doc.addPage();
      addHeaderAndFooter(4);

      // Mode1 Plot Section
      doc.setFontSize(14);
      doc.setTextColor(39, 48, 95);
      doc.text("Mode1 Plot Analysis", 10, 35, { align: "left" });

      // Line
      doc.setDrawColor(39, 48, 95);
      doc.line(10, 38, pageWidth - 10, 38);

      // Mode1 Plot Table
      const Mode1Plot = [
        ["Subject Age", this.age],
        [
          "Subject Mode1",
          !isNaN(Number(this.firstPrincipalComponent))
            ? Number(this.firstPrincipalComponent).toFixed(2)
            : "N/A",
        ],
        [
          "Percentile",
          !isNaN(Number(this.mode1Percentile))
            ? Number(this.mode1Percentile).toFixed(2)
            : "N/A",
        ],
      ];

      doc.autoTable({
        head: [["Subject Mode1 Parameters", "Value"]],
        body: Mode1Plot,
        startY: 43,
        styles: {
          fontSize: 10,
        },
        headStyles: {
          fillColor: [0, 78, 173],
          textColor: [255, 255, 255],
        },
      });

      // Mode1 Plot Chart
      const mode1plotY = doc.previousAutoTable.finalY + 8;
      if (this.mode1PlotImage) {
        const mode1plotWidth = 190;
        const mode1plotHeight = 120;
        doc.addImage(this.mode1PlotImage, "JPEG", 10, mode1plotY, mode1plotWidth, mode1plotHeight);
      } else {
        doc.setFontSize(10);
        doc.setTextColor(119, 119, 119);
        doc.text("No Mode 1 plot Available", 10, mode1plotY + 10);
      }


      ////////////////////////////////////////////////////Fifth Page/////////////////////////////////////////////////////////////////
      doc.addPage();
      addHeaderAndFooter(5);

      // Classification Section
      doc.setFontSize(14);
      doc.setTextColor(39, 48, 95);
      doc.text("Classification Results", 10, 35, { align: "left" });

      // Line
      doc.setDrawColor(39, 48, 95);
      doc.line(10, 38, pageWidth - 10, 38);
      // Mode1 Plot Chart
        if (this.classificationSnapshot) {
          const mode1plotWidth = 190;
          const mode1plotHeight = 230;
          doc.addImage(this.classificationSnapshot, "JPEG", 10, 43, mode1plotWidth, mode1plotHeight);
        } else {
          doc.setFontSize(10);
          doc.setTextColor(119, 119, 119);
          doc.text("No Mode Classification Results Available", 10, mode1plotY + 10);
        }

        // Save PDF in DB
        const pdfBlob = doc.output("blob");
        doc.save("FaceScreen_Report.pdf");
        try {
          await this.uploadPDFToBackend(pdfBlob);
        } catch (error) {
          console.error("PDF upload failed:", error);
        }
      },

      async uploadPDFToBackend(pdfBlob) {
        const fasdBackendURL = localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
        const token = localStorage.getItem("userJWtoken");

        const formData = new FormData();
        formData.append("fasdreportfile", pdfBlob, "FaceScreen_Report.pdf"); 
        formData.append("format", "PDF"); 

        const url = `${fasdBackendURL}/api/fasdReports/${this.landmark_id}?format=PDF`;

        const response = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`, 
          },
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Failed to upload PDF. Status: ${response.status}`);
        }
      }
    },
  mounted() {
    this.calculateAndSetAge();
  },
};
</script>

<style scoped>
.pdf-container {
  margin: 20px;
  text-align: center;
}
</style>